<template>
  <div class="relative overflow-x-hidden flex-1 px-6 py-6 flex">
    <div class="flex-1">
      <!-- Title Bar -->
      <div
        class="flex items-center justify-between pb-4 border-b border-gray-300"
      >
        <div class="flex items-center space-x-6">
          <h1 class="text-2xl font-semibold text-gray-800">เฟิร์มแวร์</h1>
          <button
            :disabled="formAction === 'edit'"
            :class="{ 'cursor-not-allowed': formAction === 'edit' }"
            @click="addFirmware"
            type="button"
            class="
              inline-flex
              items-center
              px-4
              py-2
              border border-transparent
              rounded-md
              shadow-sm
              text-base
              font-semibold
              tracking-wider
              text-white
              bg-cyan-600
              hover:bg-cyan-700
              focus:outline-none
              focus:ring-2
              focus:ring-offset-2
              focus:ring-cyan-500
            "
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="-ml-1 mr-2 h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                clip-rule="evenodd"
              />
            </svg>
            เพิ่มเฟิร์มแวร์
          </button>
        </div>

        <div class="flex items-center space-x-3">
          <SearchWidgetResultsList v-model="search" />

          <AppAutoComplete
            v-model="filterHardwareModel"
            :isLoading="loading"
            :isSearch="true"
            :options="hardwareModels"
            label="เลือก Device Model"
            item-text-key="title"
            item-value-key="hardware_model_code"
            class="w-60 flex-shrink-0"
          />
        </div>
      </div>
      <!-- end title bar -->

      <!-- table -->
      <div class="mt-4">
        <AppDataTable
          :isCheckbox="false"
          :options="options"
          :headers="[
            {
              text: 'Device Model',
              class: 'text-left w-36',
              value: 'hardware_model.title',
            },
            {
              text: 'เวอร์ชั่น',
              class: 'text-left w-40',
              value: 'firmware_version',
            },
            {
              text: 'ไฟล์',
              class: 'text-left w-44',
              value: 'file',
            },
            {
              text: 'ขนาดไฟล์',
              class: 'text-left w-32',
              value: 'file_size_byte',
            },
            // {
            //   text: 'Created',
            //   class: 'text-left w-44',
            //   value: 'created_at',
            // },
            {
              text: 'Updated',
              class: 'text-left w-44',
              value: 'updated_at',
            },
            {
              text: 'เฉพาะบริษัท',
              class: 'text-left',
              value: 'company',
            },
          ]"
          :items="filterFirmwares.items"
          :loading="loading"
          @sort="sort"
          :isNext="true"
          :rowClassFunction="rowClassFunction"
          @rowClick="onRowClick"
        >
          <template v-slot:file="{ item }">
            {{ item.file_name }}{{ item.file_ext }}
          </template>
          <template v-slot:created_at="{ item }">
            {{ item.created_at | formatDate }}
          </template>
          <template v-slot:updated_at="{ item }">
            {{ item.updated_at | formatDate }}
          </template>
          <template v-slot:company="{ item }">
            {{ item.company ? item.company.title_th : '-' }}
          </template>
        </AppDataTable>
      </div>
      <!-- end table -->

      <!-- pagination -->
      <div class="py-4 flex items-center justify-start">
        <AppPagination
          :options="options"
          :startNumberItem="startNumberItem"
          :endNumberItem="endNumberItem"
          :totalItems="filterFirmwares.total"
          :isPrevPage="isPrevPage"
          :isNextPage="isNextPage"
          @decreasePage="() => options.page--"
          @increasePage="() => options.page++"
          @itemsPerPageChange="() => (options.page = 1)"
        />
      </div>
      <!-- end pagination -->
    </div>

    <transition
      enter-active-class="transition ease-in-out duration-300"
      enter-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in-out duration-300"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        @click="onCloseForm"
        v-if="isOpen && formAction === 'add'"
        class="fixed inset-0 bg-gray-900 bg-opacity-30"
      />
    </transition>

    <!-- form -->
    <AppSlideOverPanel :isOpen="isOpen">
      <FormFirmware
        :formAction="formAction"
        :initFirmware="initFirmware"
        :firmware="firmware"
        @onClose="onCloseForm"
        @onCancel="onCancelForm"
        @isDataChange="(value) => (isDataFormChange = value)"
        @onDelete="onDelete"
      />
    </AppSlideOverPanel>
    <!-- end form -->

    <!-- confirm delete -->
    <AppConfirmDeleteModal
      :show="isDelete"
      :loading="deleteLoading"
      :items="deleteItems"
      :errors="deleteErrors"
      @clear="deleteErrors = []"
      @cancel="onCancelConfirmDelete"
      @submit="onSubmitConfirmDelete"
    >
      <template v-slot:info> คุณต้องลบข้อมูล Firmware? </template>
      <template v-slot="{ items }">
        <ul>
          <li
            v-for="item in items"
            :key="item.firmware_uuid"
            class="text-base text-red-500 list-disc list-inside"
          >
            {{ item.hardware_model.title }} - {{ item.firmware_version }}
          </li>
        </ul>
      </template>
    </AppConfirmDeleteModal>
    <!-- end confirm delete -->

    <!-- confirm leave page -->
    <AppConfirmLeavePageModal
      :show="isConfirmLeavePageShow"
      :type="isConfirmLeavePageType"
      :next="isConfirmLeavePageNextFunction"
      @cancel="onCancelConfirmLeavePage"
      @submit="onSubmitConfirmLeavePage"
    >
      <template v-if="isConfirmLeavePageType === 'form'" v-slot:title
        >ต้องการปิดใช่ไหม</template
      >
      <template v-if="isConfirmLeavePageType === 'form'" v-slot:message
        >คุณยังไม่ได้บันทึกข้อมูลของคุณ
        คุณแน่ใจหรือไม่ว่าต้องการปิดโดยที่ยังไม่บันทึกข้อมูล ?</template
      >
    </AppConfirmLeavePageModal>
    <!-- end confirm leave page -->
  </div>
</template>

<script>
import moment from 'moment'

import SearchWidgetResultsList from '@/components/SearchWidgetResultsList'
import AppAutoComplete from '@/components/AppAutoComplete'
import AppDataTable from '@/components/AppDataTable'
import AppPagination from '@/components/AppPagination'
import AppSlideOverPanel from '@/components/AppSlideOverPanel'
import FormFirmware from '@/components/FormFirmware'
import AppConfirmDeleteModal from '@/components/AppConfirmDeleteModal'
import AppConfirmLeavePageModal from '@/components/AppConfirmLeavePageModal'

export default {
  name: 'FirmwareList',

  components: {
    SearchWidgetResultsList,
    AppAutoComplete,
    AppDataTable,
    AppPagination,
    AppSlideOverPanel,
    FormFirmware,
    AppConfirmDeleteModal,
    AppConfirmLeavePageModal,
  },

  data() {
    return {
      loading: false,
      search: '',
      options: {
        sortBy: 'hardware_model.title',
        sortDesc: false,
        itemsPerPage: 10,
        page: 1,
      },
      filterHardwareModel: null,
      firmware: {},
      initFirmware: {},
      isOpen: false,
      formAction: '',
      isDelete: false,
      deleteLoading: false,
      deleteItems: [],
      timeout: null,
      isDataFormChange: false,
      isConfirmLeavePageShow: false,
      isConfirmLeavePageType: '',
      isConfirmLeavePageNextFunction: null,
      deleteErrors: [],
    }
  },

  filters: {
    formatDate: function (value) {
      return value ? moment(String(value)).format('DD/MM/YYYY HH:mm') : '-'
    },
  },

  watch: {
    search() {
      this.options.page = 1
    },

    filterHardwareModel() {
      this.options.page = 1
    },
  },

  computed: {
    hardwareModels() {
      return this.$store.state.hardware.hardwareModels
    },

    firmwares() {
      return this.$store.state.firmware.firmwares
    },

    filterFirmwares() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options

      let items = this.firmwares

      if (this.search) {
        items = items.filter((item) => {
          return Object.values({
            hardware_model_code: item.hardware_model.hardware_model_code,
            firmware_version: item.firmware_version,
            file: item.file_name + item.file_ext,
            file_size_byte: item.file_size_byte,
            updated_at: item.updated_at
              ? moment(item.updated_at).format('DD/MM/YYYY HH:mm')
              : '-',
            company: item.company ? item.company.title_th : '-',
          }).some((s) =>
            s.toString().toLowerCase().includes(this.search.toLowerCase())
          )
        })
      }

      if (this.filterHardwareModel) {
        items = items.filter(
          (item) =>
            item.hardware_model.hardware_model_code ===
            this.filterHardwareModel.hardware_model_code
        )
      }

      items = items.sort((a, b) => {
        let sortA = a[sortBy]
        let sortB = b[sortBy]

        if (sortBy === 'hardware_model.title') {
          sortA = a.hardware_model.title
          sortB = b.hardware_model.title
        }

        if (sortBy === 'company') {
          sortA = a.company ? a.company.title_th : '-'
          sortB = b.company ? b.company.title_th : '-'
        }

        if (sortBy === 'file') {
          sortA = a.file_name + a.file_ext
          sortB = b.file_name + b.file_ext
        }

        if (sortDesc) {
          if (sortA < sortB) return 1
          if (sortA > sortB) return -1
          return 0
        } else {
          if (sortA < sortB) return -1
          if (sortA > sortB) return 1
          return 0
        }
      })

      const total = items.length

      if (itemsPerPage > 0) {
        items = items.slice((page - 1) * itemsPerPage, page * itemsPerPage)
      }

      return { items, total }
    },

    startNumberItem() {
      if (this.filterFirmwares.total === 0) return 0

      return (this.options.page - 1) * this.options.itemsPerPage + 1
    },

    endNumberItem() {
      const sum = this.options.page * this.options.itemsPerPage
      if (sum < this.filterFirmwares.total) return sum

      return this.filterFirmwares.total
    },

    isPrevPage() {
      if (this.options.page === 1) return true

      return false
    },

    isNextPage() {
      if (this.filterFirmwares.items.length === 0) return true
      if (this.filterFirmwares.length === 0) return true

      return (
        this.options.page ===
        Math.ceil(this.filterFirmwares.total / this.options.itemsPerPage)
      )
    },
  },

  created() {
    this.$store.commit('setMenusCustomerActive', false)
    this.$store.commit('setBreadcrumbs', [
      { text: 'เฟิร์มแวร์', routeName: 'FirmwareList' },
    ])

    this.fetchDataFromApi()
  },

  methods: {
    onCancelConfirmLeavePage() {
      this.isConfirmLeavePageShow = false
    },

    onSubmitConfirmLeavePage(obj) {
      this.isDataFormChange = false
      if (obj.type === 'form') {
        this.isConfirmLeavePageShow = false
        this.isOpen = false
        this.initFirmware = {}
        this.firmware = {}
        this.formAction = ''
        return
      }
      if (obj.type === 'page') {
        this.isConfirmLeavePageShow = false
        this.isOpen = false
        this.initFirmware = {}
        this.firmware = {}
        this.formAction = ''
        this.timeout = setTimeout(() => {
          obj.next()
        }, 500)
        return
      }
    },

    onCancelForm() {
      this.isDataFormChange = false
      this.isOpen = false
      this.initFirmware = {}
      this.firmware = {}
      this.formAction = ''
    },

    onCloseForm() {
      if (this.isDataFormChange) {
        this.isConfirmLeavePageShow = true
        this.isConfirmLeavePageType = 'form'
        return
      }
      this.isOpen = false
      this.initFirmware = {}
      this.firmware = {}
      this.formAction = ''
    },

    rowClassFunction(item) {
      if (this.firmware.firmware_uuid === item.firmware_uuid) {
        return 'cursor-pointer bg-gray-200'
      }
      return 'cursor-pointer hover:bg-gray-100'
    },

    onRowClick(item) {
      if (this.firmware.firmware_uuid === item.firmware_uuid) {
        this.onCloseForm()
      } else {
        this.isOpen = true
        this.formAction = 'edit'
        this.initFirmware = {
          ...item,
          description: item.description || '',
        }
        this.firmware = {
          ...item,
          description: item.description || '',
        }
      }
    },

    async fetchDataFromApi() {
      try {
        this.loading = true

        await Promise.all([
          this.$store.dispatch('firmware/fetchFirmwares'),
          this.$store.dispatch('hardware/fetchHardwareModels'),
          this.$store.dispatch('customer/fetchCustomers'),
        ])
      } catch (error) {
        console.log(error.response)
      } finally {
        this.loading = false
      }
    },

    sort(value) {
      if (this.options.sortBy === value) {
        this.options.sortDesc = !this.options.sortDesc
        this.options.page = 1
      } else {
        this.options.sortBy = value
        this.options.sortDesc = false
        this.options.page = 1
      }
    },

    addFirmware() {
      this.isOpen = true
      this.formAction = 'add'
      this.initFirmware = {}
      this.firmware = {}
    },

    onDelete(item) {
      this.isDelete = true
      this.deleteItems = [{ ...item }]

      this.isOpen = false
    },

    onCancelConfirmDelete() {
      this.isDelete = false

      this.isOpen = true

      this.deleteErrors = []
    },

    async onSubmitConfirmDelete(items) {
      this.deleteLoading = true

      for (const item of items) {
        try {
          const payload = {
            firmware_uuid: item.firmware_uuid,
          }
          await this.$store.dispatch('firmware/deleteFirmware', payload)
        } catch (error) {
          if (error.response && error.response.data) {
            this.deleteErrors.push(
              error.response.data.message || 'Something went wrong.'
            )
            this.deleteLoading = false
            return
          }

          console.log(error.response)
        }
      }

      this.deleteLoading = false
      this.isDelete = false
      this.deleteErrors = []

      const notification = {
        type: 'success',
        message: 'Your firmware has been deleted!',
      }
      this.$store.dispatch('notification/add', notification)

      this.formAction = ''
    },
  },

  beforeDestroy() {
    clearTimeout(this.timeout)
  },

  beforeRouteLeave(to, from, next) {
    if (this.isDataFormChange) {
      this.isConfirmLeavePageShow = true
      this.isConfirmLeavePageType = 'page'
      this.isConfirmLeavePageNextFunction = next
      return
    }
    if (this.isOpen) {
      this.isOpen = false
      this.timeout = setTimeout(() => {
        next()
      }, 500)
      return
    }

    next()
  },
}
</script>
